import { useCallback, useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';

/**
 * One drag engine for every pop-up, so they all move the way an iPhone's do.
 *
 * - A top banner is flicked UP to go away; a sheet is pulled DOWN. Pulling the other way
 *   stretches a little and springs back (a rubber band), and never dismisses.
 * - It goes by distance OR speed: a long slow drag and a short fast flick both work.
 * - Once it goes, it slides off the screen and only then unmounts; otherwise it springs back.
 * - The finger is followed through a ref and requestAnimationFrame, never React state, so a
 *   drag costs no re-render.
 * - A drag that starts on a button, a field or anything marked `data-nodrag` belongs to that
 *   control: holding FIX, a slider, a game board.
 * - On a sheet that scrolls, a drag down only moves the sheet once the list is at its top, the
 *   way iOS hands a scroll over to the sheet.
 *
 * The maths is plain functions at the top, exported so it can be tested without a browser.
 */

export type Direction = 'up' | 'down';

/** Movement before we decide whether a gesture is vertical (ours) or sideways (someone else's). */
export const SLOP_PX = 8;
/** How far the rubber band can stretch, at most. */
export const RUBBER_LIMIT_PX = 24;
/** How much of the finger's travel the rubber band follows at first. */
export const RUBBER_STIFFNESS = 0.3;
/** Only the last stretch of a drag says how fast it was let go. */
export const VELOCITY_WINDOW_MS = 100;
/** Past this pull the "other" way, a banner that can open, opens. */
export const EXPAND_PX = 40;

export interface Sample {
  t: number;
  y: number;
}

/** Which way a gesture is going, once it has moved far enough to tell. */
export function axisLock(dx: number, dy: number, slop = SLOP_PX): 'x' | 'y' | null {
  if (Math.max(Math.abs(dx), Math.abs(dy)) < slop) return null;
  return Math.abs(dy) >= Math.abs(dx) ? 'y' : 'x';
}

/** A stretch that follows the finger at first and then stiffens, never passing `limit`. */
export function rubberBand(d: number, limit = RUBBER_LIMIT_PX, stiffness = RUBBER_STIFFNESS): number {
  if (d === 0) return 0;
  const x = (Math.abs(d) * stiffness) / limit;
  return Math.sign(d) * limit * (1 - 1 / (x + 1));
}

/** Where the element sits for a finger `offset` away from where the drag began. */
export function follow(offset: number, direction: Direction, dismissible: boolean): number {
  const along = direction === 'down' ? offset : -offset;
  if (dismissible && along > 0) return offset;
  return rubberBand(offset);
}

/** Speed at release, in px/ms (positive is down), from the samples in the last `windowMs`. */
export function releaseVelocity(samples: readonly Sample[], windowMs = VELOCITY_WINDOW_MS): number {
  const last = samples[samples.length - 1];
  if (!last) return 0;
  let first = last;
  for (let i = samples.length - 2; i >= 0; i--) {
    const s = samples[i]!;
    if (last.t - s.t > windowMs) break;
    first = s;
  }
  const dt = last.t - first.t;
  return dt > 0 ? (last.y - first.y) / dt : 0;
}

export interface Release {
  /** Finger travel since the drag began, in px (positive is down). */
  offset: number;
  /** px/ms, positive is down. */
  velocity: number;
  direction: Direction;
  dismissible: boolean;
  /** Distance in the dismiss direction that is enough on its own. */
  threshold: number;
  /** Speed in the dismiss direction that is enough on its own. */
  flick: number;
  /** A pull the other way past EXPAND_PX opens it (banners with a card behind them). */
  expandable?: boolean;
}

/** What a released drag does: go away, open, or spring back. */
export function releaseAction(r: Release): 'dismiss' | 'expand' | 'back' {
  const sign = r.direction === 'down' ? 1 : -1;
  const along = r.offset * sign;
  const speed = r.velocity * sign;
  if (r.dismissible) {
    // A quick flick the right way, even a short one.
    if (speed >= r.flick && along > SLOP_PX / 2) return 'dismiss';
    // Far enough, unless the finger was flung back at the end.
    if (along >= r.threshold && speed > -r.flick) return 'dismiss';
  }
  if (r.expandable && -along >= EXPAND_PX) return 'expand';
  return 'back';
}

/** How far a drag has gone towards dismissing, 0–1, for dimming a backdrop with it. */
export function dragProgress(offset: number, direction: Direction, size: number): number {
  const along = direction === 'down' ? offset : -offset;
  if (size <= 0) return 0;
  return Math.max(0, Math.min(1, along / size));
}

// ------------------------------------------------------------------------------------ hook

export const SKIP_SELECTOR = 'button, input, textarea, select, a, [data-nodrag]';

function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export interface DragOptions {
  direction: Direction;
  /** Called once the element has slid away. Leave it out and the element only rubber-bands. */
  onDismiss?: (() => void) | undefined;
  /** A pull the other way past EXPAND_PX. */
  onExpand?: (() => void) | undefined;
  /** Share of the element's height that dismisses on a slow drag (default ¼). */
  fraction?: number | undefined;
  /** A fixed distance instead of `fraction`, for small things like banners. */
  distance?: number | undefined;
  /** Flick speed that dismisses, px/ms (default 0.5). */
  flick?: number | undefined;
  /** The list inside a sheet: a drag down takes the sheet only when this is at its top. */
  scrollRef?: RefObject<HTMLElement | null> | undefined;
  /** 0–1 as the drag goes, for a backdrop that dims with it. */
  onProgress?: ((p: number, animated: boolean) => void) | undefined;
  /** A finger is down on it: pause anything that would take it away. */
  onActive?: ((active: boolean) => void) | undefined;
  enabled?: boolean | undefined;
}

interface Gesture {
  id: number;
  x: number;
  y: number;
  /** Where the finger was when the drag locked on, so it starts from there and not with a jump. */
  lockY: number;
  state: 'pending' | 'dragging' | 'ignored';
  inScroll: boolean;
  samples: Sample[];
  offset: number;
}

/**
 * Drag an element away. Attach `bind` to the part the finger holds (the whole sheet, or just
 * a header) and `ref` to the part that moves.
 */
export function useDragDismiss<T extends HTMLElement = HTMLDivElement>(options: DragOptions) {
  const ref = useRef<T | null>(null);
  const handle = useRef<HTMLElement | null>(null);
  const opts = useRef(options);
  opts.current = options;
  const gesture = useRef<Gesture | null>(null);
  const frame = useRef(0);
  const moved = useRef(false);
  const leaving = useRef(false);

  const place = useCallback((y: number, transition: string | null) => {
    const el = ref.current;
    if (!el) return;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      el.style.transition = transition ?? 'none';
      el.style.transform = y === 0 && transition ? 'translate3d(0,0,0)' : `translate3d(0,${y}px,0)`;
    });
  }, []);

  const size = () => {
    const el = ref.current;
    return el ? el.getBoundingClientRect().height : 0;
  };

  /** Slide it off the screen, then tell the owner. Also what a CLOSE or HIDE button calls. */
  const dismiss = useCallback(() => {
    const { onDismiss, direction, onProgress } = opts.current;
    if (!onDismiss || leaving.current) return;
    leaving.current = true;
    const el = ref.current;
    if (!el || reducedMotion()) {
      onProgress?.(1, false);
      onDismiss();
      return;
    }
    const away = (size() + 40) * (direction === 'down' ? 1 : -1);
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      onDismiss();
    };
    el.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 340);
    onProgress?.(1, true);
    place(away, 'transform .28s cubic-bezier(.3,.7,.4,1)');
  }, [place]);

  const springBack = useCallback(() => {
    opts.current.onProgress?.(0, true);
    place(0, reducedMotion() ? 'none' : 'transform .32s cubic-bezier(.3,1.3,.5,1)');
  }, [place]);

  const end = (commit: boolean) => {
    const g = gesture.current;
    gesture.current = null;
    opts.current.onActive?.(false);
    if (!g || g.state !== 'dragging') return;
    moved.current = true;
    if (!commit) {
      springBack();
      return;
    }
    const { direction, onDismiss, onExpand, fraction = 0.25, distance, flick = 0.5 } = opts.current;
    const action = releaseAction({
      offset: g.offset,
      velocity: releaseVelocity(g.samples),
      direction,
      dismissible: Boolean(onDismiss),
      threshold: distance ?? Math.max(40, size() * fraction),
      flick,
      expandable: Boolean(onExpand),
    });
    if (action === 'dismiss') dismiss();
    else {
      springBack();
      if (action === 'expand') onExpand?.();
    }
  };

  // React's touch listeners are passive; this one is not, so a drag can stop the page (or the
  // list inside the sheet) from scrolling or bouncing while the sheet follows the finger.
  const onTouchMove = useRef((e: TouchEvent) => {
    const g = gesture.current;
    if (!g || g.state === 'ignored') return;
    if (g.state === 'dragging') {
      e.preventDefault();
      return;
    }
    // Still deciding. Outside a list that can scroll, a vertical move is ours: stop the page from
    // taking it (a page that starts to pan cancels the pointer). In a list, only at its top and
    // heading down, so the list keeps its own scrolling and the sheet takes over from there.
    const scroller = opts.current.scrollRef?.current;
    const listScrolls = g.inScroll && scroller && scroller.scrollHeight > scroller.clientHeight + 1;
    const touch = e.touches[0];
    if (!listScrolls) {
      e.preventDefault();
      return;
    }
    if (opts.current.direction === 'down' && scroller && touch && scroller.scrollTop <= 0 && touch.clientY > g.y) {
      e.preventDefault();
    }
  }).current;

  /** The part the finger holds. Often the same element as `ref`; then set both. */
  const handleRef = useCallback(
    (el: HTMLElement | null) => {
      const prev = handle.current;
      if (prev === el) return;
      prev?.removeEventListener('touchmove', onTouchMove);
      handle.current = el;
      el?.addEventListener('touchmove', onTouchMove, { passive: false });
    },
    [onTouchMove],
  );

  useEffect(
    () => () => {
      cancelAnimationFrame(frame.current);
      handle.current?.removeEventListener('touchmove', onTouchMove);
    },
    [onTouchMove],
  );

  const bind = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      moved.current = false;
      if (opts.current.enabled === false || leaving.current) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest(SKIP_SELECTOR) && target.closest(SKIP_SELECTOR) !== e.currentTarget) return;
      const scroller = opts.current.scrollRef?.current ?? null;
      gesture.current = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        lockY: e.clientY,
        state: 'pending',
        inScroll: Boolean(scroller && scroller.contains(target)),
        samples: [{ t: e.timeStamp, y: e.clientY }],
        offset: 0,
      };
      opts.current.onActive?.(true);
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const g = gesture.current;
      if (!g || g.id !== e.pointerId || g.state === 'ignored') return;
      if (g.state === 'pending') {
        const axis = axisLock(e.clientX - g.x, e.clientY - g.y);
        if (axis === null) return;
        const down = e.clientY > g.y;
        const scroller = opts.current.scrollRef?.current;
        const listScrolls = g.inScroll && scroller && scroller.scrollHeight > scroller.clientHeight + 1;
        const listTakesIt = listScrolls && !(down && (scroller?.scrollTop ?? 0) <= 0);
        if (axis === 'x' || listTakesIt) {
          g.state = 'ignored';
          opts.current.onActive?.(false);
          return;
        }
        g.state = 'dragging';
        g.lockY = e.clientY;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* the pointer is already gone; the drag simply ends on its own */
        }
      }
      const { direction, onDismiss, onProgress } = opts.current;
      g.offset = e.clientY - g.lockY;
      g.samples.push({ t: e.timeStamp, y: e.clientY });
      if (g.samples.length > 8) g.samples.shift();
      const y = follow(g.offset, direction, Boolean(onDismiss));
      place(y, null);
      onProgress?.(dragProgress(y, direction, size()), false);
    },
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
      if (gesture.current?.id === e.pointerId) end(true);
    },
    onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => {
      if (gesture.current?.id === e.pointerId) end(false);
    },
  };

  return {
    ref,
    handleRef,
    bind,
    dismiss,
    /** True when the last gesture was a drag, so the click that follows it is not a tap. */
    wasDrag: () => moved.current,
  };
}
